import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, KPI, PageHeader, brl, num, dt } from "./_shared";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { useGlobalFilters } from "./GlobalFilters";

export default function AdminDashboard() {
  const gf = useGlobalFilters();
  const [kpis, setKpis] = useState(null);
  const [growth, setGrowth] = useState({ users: [], revenue: [] });

  useEffect(() => {
    if (!gf.loaded) return;
    const qs = gf.queryParams();
    api.get(`/admin/kpis?${qs}`).then(r => setKpis(r.data));
    const days = { "7d": 7, "30d": 30, "90d": 90, "365d": 365 }[gf.period];
    api.get(`/admin/growth?days=${days}`).then(r => setGrowth(r.data));
  }, [gf.loaded, gf.period, gf.uf, gf.plan]);

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-dashboard">
      <PageHeader
        title="Dashboard Executivo"
        subtitle={`Inteligência administrativa e financeira do Hermes${
          gf.uf || gf.plan ? ` · filtrado por ${[gf.uf, gf.plan].filter(Boolean).join(" · ")}` : ""
        }`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI label="Usuários totais" value={num(kpis?.users?.total)} testid="kpi-users-total" />
        <KPI label="Novos" value={num(kpis?.users?.new)} delta={kpis?.users?.new_delta_pct} tone="blue" testid="kpi-users-new" />
        <KPI label="Ativos (30d)" value={num(kpis?.users?.active_30d)} tone="green" testid="kpi-users-active" />
        <KPI label="Bloqueados" value={num(kpis?.users?.blocked)} tone="red" testid="kpi-users-blocked" />
        <KPI label="Assinantes ativos" value={num(kpis?.subscriptions?.active)} tone="gold" testid="kpi-subs" />
        <KPI label="Receita no período" value={brl(kpis?.revenue?.amount_cents)} delta={kpis?.revenue?.delta_pct} tone="gold" testid="kpi-revenue" />
        <KPI label="MRR" value={brl(kpis?.revenue?.mrr_cents)} tone="purple" testid="kpi-mrr" />
        <KPI label="Inadimplência" value={brl(kpis?.collections?.overdue_amount_cents)} hint={`${num(kpis?.collections?.overdue_count)} pendentes`} tone="red" testid="kpi-overdue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Evolução de cadastros" kicker="Usuários por dia" testid="chart-users">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={growth.users}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f1524", border: "1px solid #1e293b", color: "#e2e8f0" }} />
                <Bar dataKey="count" fill="#d4a24c" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Evolução da receita" kicker="R$ por dia (pagos)" testid="chart-revenue">
          <div className="h-64">
            {(growth.revenue || []).filter(r => (r.amount_cents || 0) > 0).length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="chart-revenue-empty">
                Sem receita registrada no período
              </div>
            ) : (
              <ResponsiveContainer>
                <LineChart data={growth.revenue.map(r => ({ day: r.day, amount: (r.amount_cents || 0) / 100 }))}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0f1524", border: "1px solid #1e293b", color: "#e2e8f0" }}
                    formatter={(v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v)} />
                  <Line type="monotone" dataKey="amount" stroke="#d4a24c" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="🎂 Aniversariantes de hoje" kicker="Base do Hermes" testid="card-birthdays">
          <div className="text-5xl font-serif text-[#d4a24c]">{num(kpis?.birthdays_today || 0)}</div>
          <a href="/admin/birthdays" className="mt-3 inline-block text-xs text-slate-300 hover:text-[#d4a24c]">Ver todos →</a>
        </Card>
        <Card title="Como está sua base" kicker="Snapshot" testid="card-snapshot">
          <ul className="text-sm space-y-1 text-slate-300">
            <li>Últ. atualização: <span className="text-slate-100">{dt(kpis?.as_of)}</span></li>
            <li>Assinantes cancelados: <span className="text-slate-100">{num(kpis?.subscriptions?.cancelled)}</span></li>
            <li>Novos delta: <span className="text-slate-100">{kpis?.users?.new_delta_pct == null ? "—" : `${kpis?.users?.new_delta_pct}%`}</span></li>
          </ul>
        </Card>
        <Card title="Rápido" kicker="Atalhos" testid="card-quick">
          <div className="flex flex-col gap-2 text-sm">
            <a href="/admin/users" className="text-slate-300 hover:text-[#d4a24c]">→ Todos os usuários</a>
            <a href="/admin/financial" className="text-slate-300 hover:text-[#d4a24c]">→ Painel financeiro</a>
            <a href="/admin/plans" className="text-slate-300 hover:text-[#d4a24c]">→ Gerenciar planos</a>
            <a href="/admin/coupons" className="text-slate-300 hover:text-[#d4a24c]">→ Cupons</a>
            <a href="/admin/audit" className="text-slate-300 hover:text-[#d4a24c]">→ Auditoria</a>
          </div>
        </Card>
      </div>
    </div>
  );
}
